import {
    isRangeAboveCoveredWeight,
    ProductPropertyType,
    resolveConcentrationRange,
} from '@core/domain/substance'
import { getNameOrId, ProductIdentifiers } from '@/libs/product'
import { SubstanceLists } from '@/libs/substance'
import {
    RequirementCheckStatus,
    scipNumberCheck,
    simpleRequirementChecks,
} from './requirement-checks'

const isEmptyValue = value => {
    if (value === null || typeof value === 'undefined' || value === '') {
        return true
    }

    return Array.isArray(value) && value.length === 0
}

const createFact = (id, value, details = {}) => ({ id, value, ...details })

const commonFacts = input => {
    const productContext = input.productContext || {}

    return [
        createFact('questions', input.questions.length),
        createFact(
            'answered-questions',
            input.questions.filter(question => [true, false].includes(question.state)).length,
        ),
        createFact('included-substances', input.placements.length),
        createFact(
            'general-product-statements',
            (productContext.regulatedProductStatements || []).length,
        ),
        createFact('material-categories', (productContext.materialCategoryIds || []).length),
        createFact('mixture-categories', (productContext.mixtureCategoryIds || []).length),
    ]
}

const defaultListChecker = input => ({
    checks: simpleRequirementChecks(input),
    facts: commonFacts(input),
    missingProperties: input.missingProperties,
})

const defaultPlacementMissingProperties = input => input.missingProperties || []

const candidateListPlacementMissingProperties = input => (
    resolveConcentrationRange(
        input.list,
        input.propertyValue(input.placement, ProductPropertyType.MAX_CONCENTRATION_RANGE),
    ) === null
        ? [ProductPropertyType.MAX_CONCENTRATION_RANGE]
        : []
)

const rohsPlacementMissingProperties = input => ([
    ProductPropertyType.MAX_CONCENTRATION,
    ProductPropertyType.MAX_CONCENTRATION_RANGE,
    ProductPropertyType.EXEMPTION,
].some(type => !isEmptyValue(input.propertyValue(input.placement, type)))
    ? []
    : ['rohs-concentration-or-exemption'])

const candidateListChecker = input => {
    const ranges = input.placements.map(placement => (
        input.propertyValue(placement, ProductPropertyType.MAX_CONCENTRATION_RANGE)
    ))
    const populatedRangeCount = input.placements.filter((placement, index) => (
        !isEmptyValue(ranges[index])
    )).length
    const resolvedRangeCount = ranges.filter(range => (
        resolveConcentrationRange(input.list, range) !== null
    )).length
    const aboveCoveredWeightCount = ranges.filter(range => (
        isRangeAboveCoveredWeight(input.list, range, ['scip', 'reach'])
    )).length
    const missingProperties = input.placements.some(placement => (
        candidateListPlacementMissingProperties({ ...input, placement }).length > 0
    )) ? [ProductPropertyType.MAX_CONCENTRATION_RANGE] : []

    return {
        checks: simpleRequirementChecks({ ...input, missingProperties }),
        facts: commonFacts(input).concat([
            createFact(
                'candidate-list-substances-with-concentration-range',
                populatedRangeCount,
            ),
            createFact(
                'candidate-list-substances-with-resolved-concentration-range',
                resolvedRangeCount,
            ),
            createFact(
                'candidate-list-substances-with-unresolved-concentration-range',
                populatedRangeCount - resolvedRangeCount,
            ),
            createFact(
                'candidate-list-substances-above-covered-weight',
                aboveCoveredWeightCount,
            ),
        ]),
        missingProperties,
    }
}

const rohsListChecker = input => {
    const completedPlacements = input.placements.filter(placement => (
        rohsPlacementMissingProperties({ ...input, placement }).length === 0
    ))
    const missingProperties = completedPlacements.length === input.placements.length
        ? []
        : ['rohs-concentration-or-exemption']
    const checks = simpleRequirementChecks({ ...input, missingProperties })
        .map(check => {
            if (check.id !== 'required-properties') {
                return check
            }

            return {
                ...check,
                id: 'rohs-substance-details',
                reason: check.status === RequirementCheckStatus.PASSED
                    ? {
                        messageKey: 'Page(ReceivedRequest):Component(ComplianceInformation):reason-rohs-information-complete',
                        fallbackMessage: 'RoHS concentration or exemption information is complete.',
                    }
                    : {
                        messageKey: 'Page(ReceivedRequest):Component(ComplianceInformation):reason-complete-rohs-information',
                        fallbackMessage: 'Enter a maximum concentration, concentration range, or exemption for every included RoHS substance.',
                    },
            }
        })

    return {
        checks,
        facts: commonFacts(input).concat([
            createFact('rohs-substances-with-required-information', completedPlacements.length),
            createFact(
                'rohs-substances-with-exemption',
                input.placements.filter(placement => !isEmptyValue(
                    input.propertyValue(placement, ProductPropertyType.EXEMPTION),
                )).length,
            ),
        ]),
        missingProperties,
    }
}

const scipNumberAddon = ({ context, requirement, settings }) => {
    const aboveCoveredWeightFact = (requirement.facts || []).find(fact => (
        fact.id === 'candidate-list-substances-above-covered-weight'
    ))
    const visible = settings.enableScipNumber !== false
        && requirement.list.key === SubstanceLists.CANDIDATE_LIST
        && requirement.questionState === true
    const required = visible
        && settings.scipNumberRequired === true
        && Number(aboveCoveredWeightFact && aboveCoveredWeightFact.value) > 0
    const value = getNameOrId(
        (context.product && context.product.identifiers) || [],
        ProductIdentifiers.SCIP_NUMBER,
    ) || ''

    return {
        checks: [scipNumberCheck({
            applicable: visible,
            required,
            value,
        })],
        facts: [createFact('scip-number', value, {
            applicable: visible,
            visible,
            required,
        })],
    }
}

const mergeEvaluation = (target, addition) => ({
    checks: target.checks.concat(addition.checks || []),
    facts: target.facts.concat(addition.facts || []),
    missingProperties: Array.from(new Set(
        (target.missingProperties || []).concat(addition.missingProperties || []),
    )),
})

const createRequirementCheckerRegistry = ({
    listCheckers = {},
    placementMissingPropertyResolvers = {},
    addons = [],
    includeDefaultAddons = true,
} = {}) => {
    const registeredListCheckers = {
        [SubstanceLists.CANDIDATE_LIST]: candidateListChecker,
        [SubstanceLists.ROHS]: rohsListChecker,
        ...listCheckers,
    }
    const registeredPlacementMissingPropertyResolvers = {
        [SubstanceLists.CANDIDATE_LIST]: candidateListPlacementMissingProperties,
        [SubstanceLists.ROHS]: rohsPlacementMissingProperties,
        ...placementMissingPropertyResolvers,
    }
    const registeredAddons = (includeDefaultAddons ? [scipNumberAddon] : []).concat(addons)

    return Object.freeze({
        evaluateList(input) {
            const checker = typeof registeredListCheckers[input.list.key] === 'function'
                ? registeredListCheckers[input.list.key]
                : defaultListChecker
            return checker(input)
        },
        evaluatePlacement(input) {
            const resolver = typeof registeredPlacementMissingPropertyResolvers[input.list.key]
                === 'function'
                ? registeredPlacementMissingPropertyResolvers[input.list.key]
                : defaultPlacementMissingProperties
            const missingProperties = Array.from(new Set(resolver(input)))

            return {
                status: missingProperties.length === 0 ? 'complete' : 'needs-input',
                missingProperties,
            }
        },
        applyAddons(requirement, context, settings = {}) {
            return registeredAddons.reduce((evaluation, checker) => mergeEvaluation(
                evaluation,
                checker({ requirement, context, settings }),
            ), {
                checks: requirement.checks || [],
                facts: requirement.facts || [],
                missingProperties: requirement.missingProperties || [],
            })
        },
    })
}

const complianceRequirementCheckerRegistry = createRequirementCheckerRegistry()

export {
    complianceRequirementCheckerRegistry,
    createRequirementCheckerRegistry,
    isEmptyValue,
}

export default complianceRequirementCheckerRegistry
